import { getAuthHeaders } from './auth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Generic API call function
const apiCall = async (endpoint, options = {}) => {
  const response = await fetch(`${BACKEND_URL}${endpoint}`, {
    ...options,
    headers: {
      ...getAuthHeaders(),
      ...options.headers
    }
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Request failed' }));
    throw new Error(parseErrorDetail(error, response.status));
  }

  return await response.json();
};

// Convert any FastAPI error body into a single readable string. Handles:
//   - { detail: "..." }
//   - { detail: [{ loc, msg, type }, ...] }  (Pydantic 422)
//   - { detail: { msg: "..." } }
const parseErrorDetail = (body, status) => {
  const detail = body?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail) && detail.length > 0) {
    return detail
      .map((d) => {
        if (typeof d === 'string') return d;
        const field = Array.isArray(d?.loc) ? d.loc.filter((p) => p !== 'body' && p !== 'query').join('.') : '';
        const msg = d?.msg || 'Invalid value';
        return field ? `${field}: ${msg}` : msg;
      })
      .join(' \u2022 ');
  }
  if (detail && typeof detail === 'object') return detail.msg || JSON.stringify(detail);
  if (typeof body?.message === 'string') return body.message;
  return `Request failed (HTTP ${status})`;
};

// Enquiries API
export const enquiriesAPI = {
  getAll: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiCall(`/api/enquiries?${queryString}`);
  },
  getById: (id) => apiCall(`/api/enquiries/${id}`),
  update: (id, data) => apiCall(`/api/enquiries/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data)
  }),
  updateStatus: (id, status) => apiCall(`/api/enquiries/${id}/status`, {
    method: 'PATCH',
    body: JSON.stringify({ status })
  }),
  delete: (id) => apiCall(`/api/enquiries/${id}`, { method: 'DELETE' })
};

// Jobs API
export const jobsAPI = {
  getAll: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiCall(`/api/jobs?${queryString}`);
  },
  getById: (id) => apiCall(`/api/jobs/${id}`),
  create: (data) => apiCall('/api/jobs', {
    method: 'POST',
    body: JSON.stringify(data)
  }),
  update: (id, data) => apiCall(`/api/jobs/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data)
  }),
  delete: (id) => apiCall(`/api/jobs/${id}`, { method: 'DELETE' })
};

// Customers API
export const customersAPI = {
  getAll: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiCall(`/api/customers?${queryString}`);
  },
  getById: (id) => apiCall(`/api/customers/${id}`),
  update: (id, data) => apiCall(`/api/customers/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data)
  })
};

// Dashboard API
export const dashboardAPI = {
  getSummary: () => apiCall('/api/dashboard/summary'),
  getRecentActivity: () => apiCall('/api/dashboard/recent-activity')
};
